import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  CalendarCheck,
  Timer,
  Ban,
  ShieldCheck,
  Layers,
  DollarSign,
  Gauge,
  Sparkles,
  Globe,
  Settings,
  Boxes,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import CTABand from "@/components/CTABand";
import FeatureStatusBadge from "@/components/FeatureStatusBadge";
import {
  ReservationMeter,
  BalanceChip,
  PeakCalendar,
  HouseholdCard,
} from "@/components/boat-club/BoatClubVisuals";
import { useSeo } from "@/lib/seo";
import { useReveal } from "@/lib/reveal";

/* -------------------------------- data -------------------------------- */

const OPERATOR_LOGIN_URL = "https://operator.rentanything.ai/login";

const PROBLEM_POINTS = [
  {
    title: "Spreadsheets + front-desk policing",
    body: "Reservation limits, peak rules and household captains tracked in a shared sheet. Staff become the rulebook.",
  },
  {
    title: "A few members hog every weekend",
    body: "Nothing enforces fairness on peak dates, so repeat offenders stockpile Saturdays and the rest churn.",
  },
  {
    title: "Two systems, one fleet",
    body: "Club bookings in one tool, public rentals in another — and the dock figures out double-bookings at 7 AM.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Define your plans",
    body: "Create any number of plans — Weekday, All-Access, Seasonal, Gold / Silver / Bronze. Each plan carries its own entitlements and booking rules.",
  },
  {
    step: "02",
    title: "Publish member rates",
    body: "Any rate in your price list can be marked members-only and attached to one or more plans. Your price list becomes your club calendar.",
  },
  {
    step: "03",
    title: "Members book, rules enforce",
    body: "Every member booking is checked at creation — plan entitlement, limits, balance, booking window. Rejected bookings get a plain-language reason.",
  },
];

const EXAMPLE_PLANS = [
  {
    name: "All-Access Club",
    tag: "7 days / week",
    body: "Boat every day, holidays included, at no per-trip charge. Up to 4 future reservations, only 2 on weekends/holidays. Same-day boating doesn't count against the 4.",
    extras: null,
  },
  {
    name: "Weekday Membership",
    tag: "Mon – Fri",
    body: "Monday to Friday, holidays excluded. Up to 3 future reservations. Optional last-minute weekend boating when boats are otherwise free.",
    extras: null,
  },
  {
    name: "Seasonal Club",
    tag: "May 1 – Oct 15",
    body: "Boating within a fixed season in morning and afternoon sessions. July 4th is marked peak with a limit of one holiday reservation.",
    extras: null,
  },
  {
    name: "Usage Membership",
    tag: "25 HRS · 24 mo",
    body: "25 hours that expire after 24 months. Trips 2 – 8 hours. Hours deducted at booking, returned on cancel. Overflow books at the regular cash rate.",
    extras: null,
  },
  {
    name: "Tiered Points Club",
    tag: "Platinum / Gold / Silver / Bronze",
    body: "Each tier is its own plan with its own points allowance. Each boat class has a points chart: weekday / weekend / holiday × trip length.",
    extras: [
      "Weekday bonus points",
      "Season-end expiry",
      "Points unlock as installments are paid",
      "Cancellation cutoffs",
    ],
  },
];

/* Feature-detail blocks (Available Now 1-7 from the brief).
   Each block has: eyebrow, title, body (paragraphs), bullets, Icon, and a visual.
   Rendered in an alternating left/right pattern. */
const AVAILABLE_FEATURES = [
  {
    id: "plans",
    eyebrow: "Membership plans",
    title: "Plans are a package of rules, not just a price.",
    Icon: Layers,
    paras: [
      "Create any number of plans. Each one sets the entitlements and booking rules that make it fair.",
    ],
    bullets: [
      "Active reservation limit — e.g. 4 future bookings; use one and you can book another.",
      "Peak limit — e.g. only 2 of those 4 can be weekend or holiday dates.",
      "Last-minute release window — trips starting within X hours stop counting against the limit.",
      "Captains per membership — household and corporate caps.",
      "Included allowance — hours, trips, points or dollar credit, with optional expiry.",
      "Plan status — Draft, Active, Retired. Leave any limit blank to make it unlimited.",
    ],
    Visual: () => <ReservationMeter used={3} total={4} peakUsed={2} peakTotal={2} />,
  },
  {
    id: "rates",
    eyebrow: "Member-only rates",
    title: "Your price list is your club calendar.",
    Icon: CalendarCheck,
    paras: [
      "Mark any rate as members-only and attach it to one or more plans. The rate's own settings define exactly what the plan can book.",
    ],
    bullets: [
      "Which boats — tie rates to a boat group for premium fleet or upgrades.",
      "Which dates — seasons, holidays, blackout dates.",
      "Which days — e.g. Mon–Fri only for a weekday plan.",
      "Which times — split morning and afternoon sessions to double daily capacity.",
      "How far ahead — per-rate booking window: 10, 30, 90 days or same-day only.",
      "Peak flag, minimum and maximum length, and what the member pays (cash, member rate, or units).",
    ],
    Visual: () => <PeakCalendar />,
  },
  {
    id: "balances",
    eyebrow: "Hours, trips, points & credit",
    title: "Usage-based memberships, enforced to the unit.",
    Icon: Gauge,
    paras: [
      "For clubs that sell usage instead of (or alongside) unlimited access. Four balance types: Hours, Trips, Points and Credit.",
    ],
    bullets: [
      "Allowance issued automatically on activation, with an expiry date.",
      "Staff can issue extra units or adjust a balance any time, with an expiry and a note.",
      "Rates can cost units instead of cash — availability shows the unit cost (e.g. “4 HRS”).",
      "Units are deducted the moment a booking is made. No unpaid limbo.",
      "Earliest-expiring units are used first. Cancelling returns the units automatically.",
      "Full ledger per membership — every issue, use, refund and adjustment.",
    ],
    Visual: () => (
      <div className="flex flex-col items-start gap-4">
        <BalanceChip value={21} unit="HRS" label="Remaining this season" />
        <BalanceChip value={3} unit="TRIPS" label="Peak trips remaining" />
      </div>
    ),
  },
  {
    id: "households",
    eyebrow: "Households & captains",
    title: "One membership, shared limits — the way clubs really run.",
    Icon: Users,
    paras: [
      "A membership can cover a primary member, additional captains and dependents. Everyone on it shares the limits.",
    ],
    bullets: [
      "A two-captain household on a 4-reservation plan gets 4 between them, not 8.",
      "Captain limit per plan is enforced when people are added.",
      "Per-person authorization to reserve, with start / end dates.",
      "No double-dipping — if two captains try to grab the last slot at once, limits still hold.",
    ],
    Visual: () => <HouseholdCard />,
  },
  {
    id: "lifecycle",
    eyebrow: "Lifecycle & good standing",
    title: "Statuses that actually gate what members can do.",
    Icon: ShieldCheck,
    paras: [
      "Memberships move through Pending, Active, Frozen, Suspended, Cancelled, Expired and Terminated — every change recorded with a reason.",
    ],
    bullets: [
      "Good standing — flip off for unpaid dues and bookings pause until restored.",
      "Authorized to reserve — active + paid is not enough; clear members after orientation.",
      "Start / end dates and a home location per membership.",
      "Full activity timeline — status history plus staff notes.",
    ],
    Visual: () => (
      <div
        className="rounded-2xl border border-ra-line bg-white p-5 shadow-card"
        data-testid="bc-lifecycle-mock"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ra-ink/55">
          Membership timeline
        </p>
        <ul className="mt-4 space-y-3 text-sm">
          {[
            { k: "May 03", v: "Enrolled · Pending", tone: "slate" },
            { k: "May 10", v: "Orientation complete · Authorized to reserve", tone: "ocean" },
            { k: "May 10", v: "Active · Good standing", tone: "emerald" },
            { k: "Jul 02", v: "Note: upgraded to All-Access by Rowan M.", tone: "slate" },
          ].map((row) => (
            <li key={row.k} className="flex items-start gap-3">
              <span className="shrink-0 w-14 text-[11px] font-semibold uppercase tracking-wider text-ra-ink/50">
                {row.k}
              </span>
              <span
                className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                  row.tone === "ocean"
                    ? "bg-ra-ocean"
                    : row.tone === "emerald"
                    ? "bg-emerald-500"
                    : "bg-ra-ink/30"
                }`}
              />
              <span className="text-ra-ink/80">{row.v}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: "enforcement",
    eyebrow: "Rules enforcement",
    title: "Every booking checked, with plain-language reasons.",
    Icon: Ban,
    paras: [
      "Staff or self-service — the same six checks run on every member booking. If any fails, the booking is refused with a specific reason.",
    ],
    bullets: [
      "Is the membership active, in good standing and authorized?",
      "Is this person on the membership and allowed to book?",
      "Is the date inside the membership's term?",
      "Is the plan entitled to this rate on this boat, date, day and time, within its booking window and length limits?",
      "Would this exceed the active or peak reservation limit?",
      "Is there enough balance?",
    ],
    Visual: () => (
      <div
        className="rounded-2xl border border-ra-line bg-white p-5 shadow-card space-y-3 text-sm"
        data-testid="bc-enforcement-mock"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ra-ink/55">
          Booking checks · Jul 06 · 09:00
        </p>
        {[
          { ok: true, t: "Membership active + authorized" },
          { ok: true, t: "Pontoon-26 within plan boat group" },
          { ok: false, t: "Peak limit reached (2 / 2 weekend reservations)" },
        ].map((r, i) => (
          <div
            key={i}
            className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${
              r.ok
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : "border-rose-200 bg-rose-50 text-rose-900"
            }`}
          >
            <span className="mt-0.5 text-xs font-bold">{r.ok ? "✓" : "✕"}</span>
            <span>{r.t}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "staff",
    eyebrow: "Staff tools",
    title: "A reservation wizard that already knows the rules.",
    Icon: Settings,
    paras: [
      "The front desk books through a wizard that filters down to exactly what the member's plan can book — no cross-referencing a rate sheet with a spreadsheet.",
    ],
    bullets: [
      "Plans settings page to create and edit plans.",
      "Memberships list + detail page with Overview, Captains, and Entitlement tabs.",
      "Membership picker in the reservation wizard shows only the rates that membership can book.",
      "Availability view marks member rates with a member badge and shows unit costs ('4 HRS').",
    ],
    Visual: () => (
      <div
        className="rounded-2xl border border-ra-line bg-white p-5 shadow-card"
        data-testid="bc-staff-mock"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ra-ink/55">
          Reservation wizard · Member rates
        </p>
        <ul className="mt-4 space-y-2.5 text-sm">
          {[
            { name: "Pontoon · 4 HR · Weekday", cost: "4 HRS", peak: false },
            { name: "Pontoon · 4 HR · Weekend", cost: "6 HRS", peak: true },
            { name: "Deck Boat · Full-day · Members only", cost: "Included", peak: false },
          ].map((r) => (
            <li
              key={r.name}
              className="flex items-center justify-between rounded-lg border border-ra-line bg-ra-paper px-3 py-2"
            >
              <span className="flex items-center gap-2 text-ra-ink">
                {r.peak && (
                  <span className="rounded-full bg-ra-coral/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-ra-coral">
                    Peak
                  </span>
                )}
                {r.name}
              </span>
              <span className="font-semibold text-ra-oceanDark">{r.cost}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
];

const PLATFORM_SIDE_FEATURES = [
  { Icon: Boxes, t: "One fleet", b: "Members and public renters share one availability calendar. Never double-book." },
  { Icon: ShieldCheck, t: "Digital waivers", b: "Signed before departure, attached to the booking." },
  { Icon: DollarSign, t: "Online payments", b: "Cash bookings pay online or at the desk. Deposits + holds supported." },
  { Icon: CalendarCheck, t: "Dock checklists", b: "Check-in and check-out running on the same mobile ops app as rentals." },
];

const IN_DEVELOPMENT = [
  {
    t: "Dues & payment plans",
    b: "Package price + tax, initiation fees, recurring dues (monthly / annual / installments) with early-commit deadlines, cash/check/card and card-on-file consent. Renewals and hour/trip packs. Until this ships, operators collect dues outside the platform.",
  },
  { t: "Points that unlock as members pay", b: "On an installment plan, points unlock in proportion to what's paid. 50 % paid = 50 % of season points available." },
  { t: "No boat leaves the dock with a balance owed", b: "Unpaid balance adds a 'Collect $X outstanding' step to the dock check-out checklist. Departure waits for staff to clear it." },
  { t: "Fuel to the card on file", b: "Fuel charged to the member's saved card after each trip." },
  { t: "Member self-service", b: "Members browse plans, join and pay online, cancel on their own. Join flow records agreement + initialled terms." },
  { t: "Cancellation cutoffs & no-show policy", b: "Per-rate free-cancel deadlines, non-refundable rates, staff overrides, late-cancel and no-show fees. Today, every member cancel returns units." },
  { t: "Weekday bonus points", b: "A second, smaller points allowance per plan — spent first on weekday trips." },
  { t: "Season-end expiry", b: "Points that expire on a fixed date so late- and early-joiners lose them on the same day." },
  { t: "Booker vs. user (second parties)", b: "Member books, someone else takes the boat — second party signs documents and leaves a card hold as deposit." },
  { t: "Points chart setup screen", b: "Enter the whole points chart in one grid: length × weekday/weekend × boat class, plus holiday dates and surcharges." },
  { t: "Good-standing workflow", b: "What happens to existing reservations when a membership falls behind: notices, grace, reinstatement. Today it blocks only new bookings." },
  { t: "Move a reservation in place", b: "Shift a member reservation, re-checking limits and balance, instead of cancel-and-rebook." },
];

const ROADMAP = [
  "Waitlists for a boat, class or any boat at a location, with expiring offers.",
  "Holiday and special-event lotteries with ranked preferences and tier priority.",
  "Reciprocal + multi-location limits, booking windows and fees.",
  "Per-day limits and back-to-back restrictions.",
  "Minimum booking lead time in hours — different for weekdays vs. weekends/holidays.",
  "Onboarding requirements that unlock booking automatically (agreement, waiver, boating cert, orientation, ID, age).",
  "Boat substitutions with member acceptance when a boat goes out of service.",
  "Club-cancelled trips automatically restore reservation rights and units with no penalty.",
  "Membership capacity caps, sales holds and a prospective-member waitlist.",
  "Seasonal billing vs. boating season with proration and freeze/pause.",
  "Plan upgrades and downgrades.",
  "Other post-trip charges to the card on file (damage, cleaning, late return).",
  "Prepaid account credit, drawn down by any charge.",
];

const FAQS = [
  {
    q: "Can I offer “unlimited” boating?",
    a: "Yes. Member rates can be included at no per-trip charge, and plans control fairness through active and peak reservation limits plus a last-minute release window.",
  },
  {
    q: "Can I sell hours or points instead?",
    a: "Yes. Plans can include hours, trips, points or dollar credit with expiry dates. Bookings deduct from the balance, and cancellations return it.",
  },
  {
    q: "Do family members each get their own limits?",
    a: "No, and that's deliberate. Everyone on a membership shares its limits, just like most clubs run household and corporate plans. You set how many captains each plan allows.",
  },
  {
    q: "Can I keep premium boats for higher tiers?",
    a: "Yes. Attach member rates to specific boat groups — at no charge for top tiers or an upgrade rate for others.",
  },
  {
    q: "Can I handle holidays and blackout dates?",
    a: "Yes. Exclude dates from a plan's rates, or mark holiday rates as peak so they count toward the peak limit.",
  },
  {
    q: "Can I rent the same boats to the public?",
    a: "Yes. Members and public renters share one fleet and one availability calendar.",
  },
  {
    q: "Does RentAnything bill membership dues?",
    a: "Not yet. Dues, initiation fees, payment plans (with financing charges and early-commit deadlines), cash/check/card payments and card-on-file billing are in development. Today, dues are collected outside the platform, and you can mark a membership not in good standing to pause its bookings.",
  },
  {
    q: "I run a points club with tiers. Does that work?",
    a: "Yes. Each tier is a plan with its own points allowance, and your points chart becomes points-priced member rates by boat class, day type and trip length. Weekday-only bonus points, season-end expiry and points that unlock as members pay are in development.",
  },
  {
    q: "Can a member let someone else take the boat?",
    a: "In development: the member books, and the second party signs the documents and leaves a damage-deposit card hold. Today, everyone who takes a boat on a membership must be on that membership.",
  },
  {
    q: "Can I stop boats leaving when a member owes money?",
    a: "In development: an unpaid balance adds a “collect outstanding balance” step to that day's dock check-out checklist.",
  },
  {
    q: "Can members join online?",
    a: "Self-service joining is in development. Today, staff enroll members, and members can book online through operator booking sites built on our booking API.",
  },
  {
    q: "Do you support waitlists or holiday lotteries?",
    a: "They're on our roadmap.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

/* -------------------------------- page -------------------------------- */

export default function BoatClub() {
  useSeo({
    title: "Boat Club Software — RentAnything.ai",
    description:
      "Boat clubs sell access, not single rentals. RentAnything defines your plans, publishes member rates, and enforces every reservation limit automatically on a shared fleet.",
    path: "/boat-club",
    jsonLd: FAQ_JSONLD,
  });
  useReveal();

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden ra-mesh" data-testid="boat-club-hero">
        <div className="ra-container py-16 sm:py-24 lg:py-28">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div className="animate-fade-up">
              <span className="ra-pill" data-testid="boat-club-pill">
                <span className="h-1.5 w-1.5 rounded-full bg-ra-coral" />
                New · For boat clubs
              </span>
              <h1 className="mt-5 ra-h1" data-testid="boat-club-headline">
                Sell access, not just rentals.{" "}
                <span className="text-ra-ocean">RentAnything enforces your club's rules</span>{" "}
                on every booking.
              </h1>
              <p className="mt-5 ra-lead max-w-xl" data-testid="boat-club-sub">
                Define your plans — reservation limits, peak limits, which boats, which days, hours or points — and every member booking is checked automatically on the same fleet you rent to the public.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact?vertical=boat_club"
                  className="ra-btn-primary"
                  data-testid="boat-club-hero-demo-btn"
                >
                  Book a Demo <ArrowRight size={16} />
                </Link>
                <a
                  href={OPERATOR_LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ra-btn-ghost"
                  data-testid="boat-club-hero-operator-login-btn"
                >
                  Operator Login
                </a>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-ra-ink/55">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-ra-ocean" /> Rules enforced automatically
                </div>
                <div className="flex items-center gap-2">
                  <Boxes size={14} className="text-ra-amber" /> One fleet, one calendar
                </div>
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="relative w-full max-w-sm">
                <div
                  className="absolute -inset-6 rounded-[2rem] pointer-events-none"
                  aria-hidden="true"
                  style={{
                    background:
                      "radial-gradient(55% 55% at 20% 20%, rgba(130,180,195,0.25) 0%, rgba(130,180,195,0) 70%), radial-gradient(45% 45% at 90% 90%, rgba(245,158,11,0.12) 0%, rgba(245,158,11,0) 70%)",
                  }}
                />
                <div className="relative grid gap-4">
                  <ReservationMeter used={3} total={4} peakUsed={2} peakTotal={2} />
                  <div className="flex justify-end">
                    <BalanceChip value={21} unit="HRS" label="Remaining this season" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="ra-section" data-testid="boat-club-problem">
        <div className="ra-container">
          <div className="max-w-2xl reveal">
            <span className="ra-eyebrow">The problem</span>
            <h2 className="mt-3 ra-h2">Three things that break every busy season.</h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {PROBLEM_POINTS.map((p) => (
              <div
                key={p.title}
                className="rounded-2xl border border-ra-line bg-white p-6 shadow-card reveal"
              >
                <p className="font-display text-lg font-semibold text-ra-ink">{p.title}</p>
                <p className="mt-2 text-sm text-ra-ink/70">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="ra-section bg-ra-paper border-y border-ra-line" data-testid="boat-club-how">
        <div className="ra-container">
          <div className="max-w-2xl reveal">
            <span className="ra-eyebrow">How it works</span>
            <h2 className="mt-3 ra-h2">Three steps from plan to enforced booking.</h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {HOW_IT_WORKS.map((s) => (
              <div
                key={s.step}
                className="rounded-2xl border border-ra-line bg-white p-6 shadow-card reveal"
                data-testid={`boat-club-step-${s.step}`}
              >
                <p className="font-display text-sm font-bold tracking-[0.2em] text-ra-ocean">{s.step}</p>
                <p className="mt-3 font-display text-xl font-semibold text-ra-ink">{s.title}</p>
                <p className="mt-2 text-sm text-ra-ink/70">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXAMPLE PLANS */}
      <section className="ra-section" data-testid="boat-club-plans">
        <div className="ra-container">
          <div className="flex flex-wrap items-end justify-between gap-4 reveal">
            <div className="max-w-2xl">
              <span className="ra-eyebrow">Run any club model</span>
              <h2 className="mt-3 ra-h2">Examples you can build. Your dues, your way.</h2>
              <p className="mt-4 ra-lead">
                These aren't templates — they're plans an operator configures from the same primitives. Mix, rename and stack them however your club actually runs.
              </p>
            </div>
            <FeatureStatusBadge status="available" size="md" />
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {EXAMPLE_PLANS.map((p) => (
              <div
                key={p.name}
                className="rounded-2xl border border-ra-line bg-white p-6 shadow-card reveal flex flex-col"
                data-testid={`boat-club-plan-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="font-display text-lg font-semibold text-ra-ink">{p.name}</p>
                  <span className="rounded-full border border-ra-line bg-ra-paper px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ra-ink/60 whitespace-nowrap">
                    {p.tag}
                  </span>
                </div>
                <p className="mt-3 text-sm text-ra-ink/70 flex-1">{p.body}</p>
                {p.extras && (
                  <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-900">
                        Extras in progress
                      </p>
                      <FeatureStatusBadge status="in_development" />
                    </div>
                    <ul className="mt-2 space-y-1 text-xs text-amber-900/90">
                      {p.extras.map((x) => (
                        <li key={x}>· {x}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE DETAIL BLOCKS — Available Now */}
      <section className="ra-section bg-ra-paper border-y border-ra-line" data-testid="boat-club-features">
        <div className="ra-container">
          <div className="max-w-2xl reveal">
            <span className="ra-eyebrow">Available now</span>
            <h2 className="mt-3 ra-h2">The operator surface that runs the club.</h2>
            <p className="mt-4 ra-lead">
              Everything below is live in the platform today. “In development” and “Roadmap” items live at the bottom of the page.
            </p>
          </div>

          <div className="mt-14 space-y-20">
            {AVAILABLE_FEATURES.map((f, i) => {
              const reverse = i % 2 === 1;
              const Icon = f.Icon;
              const Visual = f.Visual;
              return (
                <div
                  key={f.id}
                  className={`grid items-center gap-10 lg:gap-14 lg:grid-cols-2 reveal ${
                    reverse ? "lg:[&>*:first-child]:order-2" : ""
                  }`}
                  data-testid={`boat-club-feature-${f.id}`}
                >
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ra-ocean/15 text-ra-oceanDark">
                        <Icon size={18} />
                      </span>
                      <FeatureStatusBadge status="available" />
                    </div>
                    <p className="mt-4 ra-eyebrow">{f.eyebrow}</p>
                    <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-ra-ink leading-tight">
                      {f.title}
                    </h3>
                    {f.paras.map((p, idx) => (
                      <p key={idx} className="mt-4 text-base text-ra-ink/75">
                        {p}
                      </p>
                    ))}
                    <ul className="mt-5 space-y-2.5 text-sm text-ra-ink/80">
                      {f.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2.5">
                          <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-ra-ocean" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-center">
                    <div className="w-full max-w-sm">
                      <Visual />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAIRNESS CALLOUT */}
      <section className="ra-section" data-testid="boat-club-fairness">
        <div className="ra-container">
          <div
            className="relative overflow-hidden rounded-3xl border border-ra-line p-10 sm:p-14 shadow-card reveal"
            style={{ background: "#F5F8FC" }}
          >
            <div
              className="absolute inset-0 pointer-events-none"
              aria-hidden="true"
              style={{
                background:
                  "radial-gradient(55% 55% at 100% 0%, rgba(130,180,195,0.30) 0%, rgba(130,180,195,0) 65%), radial-gradient(40% 40% at 0% 100%, rgba(245,158,11,0.10) 0%, rgba(245,158,11,0) 65%)",
              }}
            />
            <div className="relative grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
              <div>
                <div className="flex items-center gap-2">
                  <Timer size={16} className="text-ra-ocean" />
                  <span className="ra-eyebrow">Fairness, enforced</span>
                </div>
                <h2 className="mt-3 ra-h2">Unlimited boating. Fair weekends.</h2>
                <p className="mt-5 ra-lead">
                  Active reservation limits stop stockpiling. Peak limits cap how many of those can be weekend or holiday dates. The last-minute release window lets same-day trips flow freely without counting against the limit — so boats that would sit at the dock get used, and nobody can grab the whole summer in April.
                </p>
                <ul className="mt-5 space-y-2 text-sm text-ra-ink/80">
                  <li className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-ra-ocean" /> Active limit — e.g. 4 future bookings
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-ra-ocean" /> Peak limit — e.g. only 2 of those 4 on peak dates
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-ra-ocean" /> Last-minute release — trips within X hours stop counting
                  </li>
                </ul>
              </div>
              <div className="flex items-center justify-center">
                <div className="w-full max-w-sm">
                  <PeakCalendar />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ONE FLEET / ONE CALENDAR */}
      <section className="ra-section bg-ra-paper border-y border-ra-line" data-testid="boat-club-one-fleet">
        <div className="ra-container">
          <div className="max-w-2xl reveal">
            <span className="ra-eyebrow">One fleet, one calendar</span>
            <h2 className="mt-3 ra-h2">Club and public rentals on the same boats.</h2>
            <p className="mt-4 ra-lead">
              A member booking is a rental. It uses the same boats, availability and check-in flow as a public rental. The only differences are which rates the member can see and how the booking is paid for.
            </p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PLATFORM_SIDE_FEATURES.map((f) => {
              const Icon = f.Icon;
              return (
                <div
                  key={f.t}
                  className="rounded-2xl border border-ra-line bg-white p-5 shadow-card reveal"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ra-ocean/15 text-ra-oceanDark">
                    <Icon size={16} />
                  </span>
                  <p className="mt-3 font-display text-base font-semibold text-ra-ink">{f.t}</p>
                  <p className="mt-1.5 text-sm text-ra-ink/70">{f.b}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOR YOUR OWN BOOKING SITE */}
      <section className="ra-section" data-testid="boat-club-booking-site">
        <div className="ra-container">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
            <div className="reveal">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ra-ocean/15 text-ra-oceanDark">
                  <Globe size={18} />
                </span>
                <FeatureStatusBadge status="available" />
              </div>
              <p className="mt-4 ra-eyebrow">Built for online member booking</p>
              <h2 className="mt-2 ra-h2">Members book from your own site, with full rules on the API.</h2>
              <p className="mt-5 ra-lead">
                A signed-in member can see their memberships, check eligibility, current usage against limits and remaining balance, see member rates in availability, get a member quote and book. Bookings paid in hours, trips or points need no payment step.
              </p>
              <p className="mt-3 text-sm text-ra-ink/60">
                For operators who run their own booking site. A hosted member portal is in development.
              </p>
            </div>
            <div className="reveal">
              <div
                className="rounded-2xl border border-dashed border-ra-line bg-ra-paper px-6 py-10 text-center text-xs text-ra-ink/50"
                data-testid="boat-club-screenshot-placeholder"
              >
                [[SCREENSHOT: Operator booking site with member rate badge, "4 HRS" unit cost and balance chip visible]]
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S NEXT — compact, visually secondary */}
      <section className="ra-section bg-ra-paper border-y border-ra-line" data-testid="boat-club-next">
        <div className="ra-container">
          <div className="max-w-2xl reveal">
            <span className="ra-eyebrow">What's next</span>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-ra-ink">
              Shipping soon and on the horizon.
            </h2>
            <p className="mt-3 text-sm text-ra-ink/65">
              Clearly labeled so you know what to count on today vs. what to plan around. No dates attached — we only commit to what's already live.
            </p>
          </div>

          <div className="mt-10 grid gap-10 lg:grid-cols-2">
            <div data-testid="boat-club-in-development">
              <div className="flex items-center gap-2">
                <FeatureStatusBadge status="in_development" size="md" />
                <span className="text-xs text-ra-ink/55">{IN_DEVELOPMENT.length} items</span>
              </div>
              <ul className="mt-5 space-y-3">
                {IN_DEVELOPMENT.map((f) => (
                  <li
                    key={f.t}
                    className="rounded-xl border border-ra-line bg-white px-4 py-3 shadow-sm"
                  >
                    <p className="font-display text-sm font-semibold text-ra-ink">{f.t}</p>
                    <p className="mt-1 text-xs text-ra-ink/65">{f.b}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="boat-club-roadmap">
              <div className="flex items-center gap-2">
                <FeatureStatusBadge status="roadmap" size="md" />
                <span className="text-xs text-ra-ink/55">{ROADMAP.length} items</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-ra-ink/75">
                {ROADMAP.map((r) => (
                  <li
                    key={r}
                    className="flex items-start gap-2.5 rounded-lg border border-ra-line/70 bg-white/60 px-3 py-2"
                  >
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ra-section" data-testid="boat-club-faq">
        <div className="ra-container max-w-3xl">
          <div className="reveal">
            <span className="ra-eyebrow">FAQ</span>
            <h2 className="mt-3 ra-h2">Questions operators actually ask.</h2>
          </div>
          <div className="mt-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`q-${i}`} data-testid={`boat-club-faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base font-semibold text-ra-ink">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-ra-ink/75">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CLOSING CTA — custom overrides so primary deep-links the vertical */}
      <CTABand
        eyebrow="Ready to run your club like one system?"
        title="See it configured on your actual fleet."
        sub="30-minute demo, your plans, your rate card. We'll build one of them live on the call."
        primaryTo="/contact?vertical=boat_club"
      />
    </>
  );
}
